import React, { useState, useEffect } from "react";
import { TextField, TextFieldProps } from "@mui/material";

interface TextFieldWithCharacterCountProps extends Omit<TextFieldProps, "inputProps"> {
  charLimit?: number;
}

export default function TextFieldWithCharacterCount({
  charLimit,
  value,
  defaultValue,
  onChange,
  helperText,
  ...otherProps
}: TextFieldWithCharacterCountProps) {
  const [currentValue, setCurrentValue] = useState<string>((value as string) || (defaultValue as string) || "");

  useEffect(() => {
    if (value !== undefined) {
      setCurrentValue(value as string);
    }
  }, [value]);

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = event.target.value;

    if (charLimit && newValue.length > charLimit) {
      return;
    }

    setCurrentValue(newValue);

    if (onChange) {
      onChange(event);
    }
  };

  const currentLength = currentValue.length;
  const isOverLimit = charLimit ? currentLength > charLimit : false;
  const remainingChars = charLimit ? charLimit - currentLength : 0;

  let finalHelperText = helperText;
  if (charLimit) {
    const charCountText = `${currentLength}/${charLimit} characters`;
    const remainingText =
      remainingChars >= 0 ? ` (${remainingChars} remaining)` : ` (${Math.abs(remainingChars)} over limit)`;

    if (helperText) {
      finalHelperText = `${helperText} • ${charCountText}${remainingText}`;
    } else {
      finalHelperText = `${charCountText}${remainingText}`;
    }
  }

  return (
    <TextField
      {...otherProps}
      value={currentValue}
      onChange={handleChange}
      error={otherProps.error || isOverLimit}
      helperText={finalHelperText}
    />
  );
}
